var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { AttributionControl, GeolocateControl, MapProvider, NavigationControl, ScaleControl } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import { states } from './us-bbox';
import { debounce } from 'lodash';
export default function BaseMap(_a) {
    var globalConfig = _a.globalConfig, mapConfig = _a.mapConfig, layers = _a.layers, mapRef = _a.mapRef, children = _a.children, mapProps = __rest(_a, ["globalConfig", "mapConfig", "layers", "mapRef", "children"]);
    var uiRef = useRef(null);
    var inputOptions = useState({
        dragRotate: false,
    })[0];
    var defaultBounds = states[globalConfig.stateAbbr || 'CA'];
    var defaultViewState = mapProps.initialViewState || {
        padding: {
            left: 44,
            top: 44,
            right: 44,
            bottom: 44,
        }
    };
    var boundMap = function () {
        var map = mapRef.current;
        var newBounds = mapProps.currentBounds || defaultBounds;
        if (map && newBounds) {
            setTimeout(function () {
                map.fitBounds(newBounds, {
                    linear: true,
                    duration: 1500,
                });
            }, 0);
        }
    };
    var _b = useState(defaultViewState), viewState = _b[0], setViewState = _b[1];
    useEffect(function () {
        boundMap();
    }, [mapProps.currentBounds]);
    useEffect(function () {
        boundMap();
    }, [mapProps.resetBounds]);
    useEffect(function () {
        boundMap();
    }, [viewState.paddingChanged]);
    var handleLoad = function (event) {
        var map = event.target;
        // Animated zoom in effect on page load
        if (map && defaultBounds) {
            updatePadding();
            map.fitBounds(defaultBounds, { animate: false });
            map.zoomOut({ animate: false });
            map.fitBounds(defaultBounds, {
                duration: 3000
            });
        }
        if (typeof mapProps.onLoad == 'function')
            mapProps.onLoad(event);
    };
    var updatePadding = function () {
        var ui = uiRef.current;
        var safeArea = ui === null || ui === void 0 ? void 0 : ui.querySelector('.EventPage__mapUiActiveCol');
        var uiClientRect = ui === null || ui === void 0 ? void 0 : ui.getBoundingClientRect();
        var safeClientRect = safeArea === null || safeArea === void 0 ? void 0 : safeArea.getBoundingClientRect();
        var uiDimensions = uiClientRect || { top: 0, right: 0, bottom: 0, left: 0 };
        var safeDimensions = safeClientRect || { top: 0, right: 0, bottom: 0, left: 0 };
        var newPadding = {
            bottom: uiDimensions.bottom - safeDimensions.bottom + 44,
            left: uiDimensions.left - safeDimensions.left + 44,
            right: uiDimensions.right - safeDimensions.right + 44,
            top: uiDimensions.top - safeDimensions.top + 44,
        };
        // If no padding changed stop the update
        if (viewState.padding.bottom == newPadding.bottom
            && viewState.padding.left == newPadding.left
            && viewState.padding.right == newPadding.right
            && viewState.padding.top == newPadding.top)
            return;
        setViewState(function () { return (__assign(__assign({}, viewState), { padding: {
                top: uiDimensions.top - safeDimensions.top + 44,
                right: uiDimensions.right - safeDimensions.right + 44,
                bottom: uiDimensions.bottom - safeDimensions.bottom + 44,
                left: uiDimensions.left - safeDimensions.left + 44,
            }, paddingChanged: viewState.paddingChanged + 1 })); });
    };
    var handleResize = useMemo(function () { return debounce(updatePadding, 200); }, []);
    var onHover = useCallback(function (event) {
        if (typeof mapProps.onHover == 'function')
            mapProps.onHover(event);
    }, []);
    var onMouseOut = useCallback(function (event) {
        if (typeof mapProps.onMouseOut == 'function')
            mapProps.onMouseOut(event);
    }, []);
    return (React.createElement(MapProvider, null,
        React.createElement("div", { className: "EventPage p-3 px-md-3 py-md-0" },
            React.createElement("div", { className: "EventPage__mapBg" },
                React.createElement(Map, __assign({ ref: mapRef }, viewState, inputOptions, mapProps, { onMove: function (event) { return setViewState(event.viewState); }, onResize: handleResize, onMouseMove: onHover, onMouseOut: onMouseOut, onLoad: handleLoad, mapLib: maplibregl, mapStyle: mapConfig.vectorStyle || "https://api.maptiler.com/maps/basic-v2-light/style.json?key=UkL1VJnNaQc7rdAvfw5r" }),
                    Boolean(!mapConfig.vectorStyle) && (React.createElement(AttributionControl, { compact: true, customAttribution: "Using developer map, please define map.vectorStyle in the page config.", position: 'top-left' })),
                    React.createElement(GeolocateControl, { position: 'top-left' }),
                    React.createElement(ScaleControl, { unit: 'imperial', position: 'bottom-left' }),
                    React.createElement(NavigationControl, { position: 'top-left', showCompass: false }),
                    layers)),
            React.createElement("div", { ref: uiRef, className: "EventPage__mapUi h-100" }, children))));
}
